import type React from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { AuthLayout } from "../../../auth";
import { CheckLineIcon, CloseLineIcon } from "../../../../icons";
import Button from "../../../../shared/ui/button/Button";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";
import { formatEuros } from "../../domain/modulePrices";

export default function ComparisonPage() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { isMobile } = useBreakpoint();
    const language = i18n.language || "es";
    // What a module adds to the plan each month. The figures are the
    // published ones in `modulePrices.ts`, formatted for the reader.
    const modulePrice = (cents: number) =>
        `${t("subscription.compare.modulePrice", { price: formatEuros(cents, language) })} ${t(
            "subscription.billing.tax.suffix"
        )}`;

    /* eslint-disable @typescript-eslint/no-explicit-any */
    // The same table as alchely.com/es/plans, so the two cannot drift: a
    // therapist who compared the plans before signing up should find the same
    // answer inside. `price` is what a module adds on top of Plus.
    const rows: {
        key: string;
        basic: unknown;
        plus: unknown;
        /**
         * Max's cell, only where it differs from Plus. Max holds everything
         * Plus does at the same prices, so every other row repeats Plus's.
         */
        max?: unknown;
        price?: string;
    }[] = [
        {
            key: "notes",
            basic: t("subscription.compare.notes.basic", { returnObjects: true }),
            plus: t("subscription.compare.notes.plus", { returnObjects: true }),
        },
        {
            key: "templates",
            basic: t("subscription.compare.templates.basic", { returnObjects: true }),
            plus: t("subscription.compare.templates.plus", { returnObjects: true }),
        },
        {
            // The one difference between Plus and Max, said where the plans
            // are compared rather than left for the billing screen.
            key: "sessions",
            basic: t("subscription.compare.sessions.basic"),
            plus: t("subscription.compare.sessions.plus"),
            max: t("subscription.compare.sessions.max"),
        },
        { key: "patientPortal", basic: true, plus: true },
        { key: "patientTypes", basic: true, plus: true },
        {
            key: "augmentedMemory",
            basic: false,
            plus: t("subscription.compare.augmentedMemory.plus", { returnObjects: true }),
            price: modulePrice(750),
        },
        {
            key: "scheduler",
            basic: false,
            plus: t("subscription.compare.scheduler.plus", { returnObjects: true }),
        },
        {
            key: "alchelyMeeting",
            basic: false,
            plus: t("subscription.compare.alchelyMeeting.plus", { returnObjects: true }),
            price: modulePrice(750),
        },
        {
            key: "constellation",
            basic: false,
            plus: t("subscription.compare.constellation.plus", { returnObjects: true }),
            // Sold as systemic therapy, together with Genogram & Ecomap.
            price: t("subscription.compare.systemicPrice", {
                price: formatEuros(499, language),
            }),
        },
        {
            key: "systemicMap",
            basic: false,
            plus: t("subscription.compare.systemicMap.plus", { returnObjects: true }),
            price: t("subscription.compare.includedInSystemic"),
        },
        {
            key: "emdr",
            basic: false,
            plus: t("subscription.compare.emdr.plus", { returnObjects: true }),
            price: modulePrice(499),
        },
        {
            key: "meditation",
            basic: false,
            plus: t("subscription.compare.meditation.plus", { returnObjects: true }),
        },
        {
            key: "futureTools",
            basic: false,
            plus: t("subscription.compare.futureTools.plus", { returnObjects: true }),
        },
        {
            key: "support",
            basic: t("subscription.compare.support.basic"),
            plus: t("subscription.compare.support.plus"),
        },
    ];

    const renderContent = (content: unknown, isMobile = false) => {
        if (typeof content === "boolean") {
            return content ? (
                <CheckLineIcon className="size-5 text-brand-500" />
            ) : (
                <CloseLineIcon className="size-5 text-gray-300 dark:text-gray-600" />
            );
        }
        if (Array.isArray(content)) {
            return (
                <ul className={`list-disc ${isMobile ? "pl-5" : "pl-4"} space-y-1 text-left`}>
                    {content.map((item, i) => (
                        <li key={i}>{String(item)}</li>
                    ))}
                </ul>
            );
        }
        return content as React.ReactNode;
    };

    /* What a module costs on top of the plan. A chip rather than one more
       bullet: it is the only line in the cell that is about money, and a
       reader scanning the Plus column should not have to find it among the
       features. */
    const priceChip = (price: string) => (
        <span className="mt-2 inline-block rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700 dark:bg-brand-500/15 dark:text-brand-300">
            {price}
        </span>
    );

    return (
        <div className="min-h-screen w-full relative bg-gray-50 dark:bg-gray-900 overflow-y-auto">
            <AuthLayout hideLogo={false} hideVisualPanel={true} wrapperClassName="max-w-screen-xl">
                <div className="w-full mx-auto px-4 py-8 sm:py-12 sm:px-6 lg:px-8">
                    <div className="text-center mb-8 sm:mb-12">
                        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-content-primary lg:text-4xl">
                            {t("subscription.compare.title")}
                        </h2>
                    </div>

                    {isMobile ? (
                        <div className="space-y-4">
                            {rows.map((feature, index) => (
                                <div key={index} className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
                                    <div className="p-4 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                                        <h3 className="font-bold text-content-primary">
                                            {t(`subscription.compare.features.${feature.key}`)}
                                        </h3>
                                    </div>
                                    <div className="grid grid-cols-3 divide-x divide-gray-100 dark:divide-gray-700">
                                        <div className="p-4">
                                            <p className="text-xs font-bold uppercase tracking-wider text-content-tertiary mb-3">
                                                {t("subscription.free.title")}
                                            </p>
                                            <div className="text-xs text-content-secondary leading-relaxed">
                                                {renderContent(feature.basic, true)}
                                            </div>
                                        </div>
                                        <div className="p-4 bg-brand-50/20 dark:bg-brand-900/5">
                                            <p className="text-xs font-bold uppercase tracking-wider text-brand-600 mb-3">
                                                {t("subscription.plus.title")}
                                            </p>
                                            <div className="text-xs font-medium leading-relaxed text-content-primary">
                                                {renderContent(feature.plus, true)}
                                                {feature.price && priceChip(feature.price)}
                                            </div>
                                        </div>
                                        <div className="p-4 bg-brand-900/5 dark:bg-brand-950/30">
                                            <p className="text-xs font-bold uppercase tracking-wider text-brand-800 dark:text-brand-300 mb-3">
                                                {t("subscription.max.title")}
                                            </p>
                                            <div className="text-xs font-medium leading-relaxed text-content-primary">
                                                {feature.max !== undefined ? (
                                                    <span className="font-bold text-brand-800 dark:text-brand-300">
                                                        {renderContent(feature.max, true)}
                                                    </span>
                                                ) : (
                                                    renderContent(feature.plus, true)
                                                )}
                                                {feature.price && priceChip(feature.price)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="overflow-x-auto -mx-4 sm:mx-0 bg-white sm:rounded-2xl shadow-sm dark:bg-gray-800 border-y sm:border border-gray-200 dark:border-gray-700">
                            <div className="min-w-[640px] sm:min-w-full">
                                <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                                    <div className="p-4 sm:p-6 font-semibold text-content-primary sticky left-0 bg-gray-50 dark:bg-gray-900/50 z-10 border-r border-gray-100 dark:border-gray-800">
                                        {t("subscription.compare.feature")}
                                    </div>
                                    <div className="p-4 sm:p-6 font-semibold text-center text-content-primary">{t("subscription.free.title")}</div>
                                    <div className="p-4 sm:p-6 flex flex-col justify-center items-center text-center text-brand-700 bg-brand-50/50 dark:bg-brand-900/10 dark:text-brand-400 border-l border-gray-200 dark:border-gray-700">
                                        <span className="font-semibold text-sm sm:text-lg">{t("subscription.plus.title")}</span>
                                        <span className="text-[9px] sm:text-xs font-normal text-brand-600/80 mt-1">{t("subscription.plus.includesBasic")}</span>
                                    </div>
                                    {/* Max: the darker end of the same palette, as on the plans page. */}
                                    <div className="p-4 sm:p-6 flex flex-col justify-center items-center text-center bg-gradient-to-br from-brand-800 to-brand-950 text-white">
                                        <span className="font-semibold text-sm sm:text-lg">{t("subscription.max.title")}</span>
                                        <span className="text-[9px] sm:text-xs font-normal text-white/70 mt-1">{t("subscription.max.includesPlus")}</span>
                                    </div>
                                </div>

                                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {rows.map((feature, index) => (
                                        <div key={index} className="grid grid-cols-[1.4fr_1fr_1fr_1fr] hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors">
                                            <div className="p-4 sm:p-6 flex items-center text-xs sm:text-sm font-medium text-content-primary sticky left-0 bg-white dark:bg-gray-800 z-10 border-r border-gray-100 dark:border-gray-800">
                                                {t(`subscription.compare.features.${feature.key}`)}
                                            </div>
                                            <div className="p-4 sm:p-6 flex justify-center items-center text-xs sm:text-sm text-content-secondary text-center">
                                                {renderContent(feature.basic)}
                                            </div>
                                            <div className="flex flex-col items-center justify-center border-l border-gray-100 bg-brand-50/30 p-4 text-center text-xs font-medium text-content-primary dark:border-gray-800 dark:bg-brand-500/5 sm:p-6 sm:text-sm">
                                                {renderContent(feature.plus)}
                                                {feature.price && priceChip(feature.price)}
                                            </div>
                                            {/* The same as Plus except where Max differs, which is
                                                said in bold so the difference is what the eye finds. */}
                                            <div className="flex flex-col items-center justify-center border-l border-gray-100 bg-brand-900/5 p-4 text-center text-xs font-medium text-content-primary dark:border-gray-800 dark:bg-brand-950/30 sm:p-6 sm:text-sm">
                                                {feature.max !== undefined ? (
                                                    <span className="text-base font-bold text-brand-800 dark:text-brand-300 sm:text-lg">
                                                        {renderContent(feature.max)}
                                                    </span>
                                                ) : (
                                                    renderContent(feature.plus)
                                                )}
                                                {feature.price && priceChip(feature.price)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="mt-6 space-y-2 text-center">
                        <p className="text-sm font-semibold text-brand-700 dark:text-brand-300">
                            {t("subscription.compare.annualNote")}
                        </p>
                        <p className="text-xs text-content-tertiary sm:text-sm">
                            {t("subscription.compare.modulesNote")}
                        </p>
                        <p className="text-xs text-content-tertiary sm:text-sm">
                            {t("subscription.billing.tax.note")}
                        </p>
                    </div>

                    <div className="mt-8 flex justify-center">
                        <Button onClick={() => navigate("/subscription")}>
                            {t("subscription.compare.choosePlan")}
                        </Button>
                    </div>
                </div>
            </AuthLayout>
        </div>
    );
}
